import { DEPOSITOR_ABANDONMENT_SORTS, REVIEW_REASONS, WITHDRAWAL_STATUSES } from '@pf/contracts';
import {
  ABANDONMENT_PAGE_SIZE,
  HISTORY_PAGE_SIZE,
  WITHDRAWALS_PAGE_SIZE,
} from '@/lib/api/ops-queries';
import { PAGE_SIZES } from '@/lib/pagination';
import { listHref, readParam, type ListStateDef } from './list-state';

/**
 * What each operator list screen keeps in its URL.
 *
 * One definition per screen, and it is the whole contract: the vocabulary a param may take, the
 * view the screen opens on, and the path the state belongs to. The screens read their filters
 * from here, `return-to.ts` rebuilds a return address from here, and the readers below are the
 * only thing that decides whether something in the address bar is meaningful — so a value that
 * exists in the URL is a value the screen genuinely offers.
 *
 * `dir` is deliberately absent from the defaults. Each screen's natural order depends on what it
 * is sorted by — the waiting queue reads oldest-first, a log reads newest-first — so "no `dir` in
 * the URL" means "this sort's own order", and switching tab or sort simply drops the param
 * instead of having to remember which direction to substitute.
 */

/** How long a free-text filter may be in the URL. Comfortably past any real search. */
const SEARCH_MAX = 100;

/** The sort directions every list understands. */
export const SORT_DIRS = ['ASC', 'DESC'] as const;

/** A sort direction, or `undefined` for the current sort's natural order. */
export type SortDir = (typeof SORT_DIRS)[number];

// --- Unified lookup ----------------------------------------------------------------------------

/** How long the lookup box may hold, mirroring `OpsLookupQuerySchema`'s own `q` cap. */
const LOOKUP_MAX = 120;

/** The unified lookup's view state: one free-text box, debounced into the URL. */
export interface LookupListState {
  /** The pasted query — a transaction/withdrawal id, a lock reference, an amount, or a username. */
  readonly q: string;
}

/**
 * The unified lookup's URL contract (`/ops/lookup`).
 *
 * One param, `q`, so a search an operator ran can be linked and reloaded exactly like any other
 * screen's filter — the server does the work of deciding what the pasted text *is*
 * (`OPS_LOOKUP_KINDS`); this screen only carries the box's contents.
 */
export const LOOKUP_LIST: ListStateDef<LookupListState> = {
  path: '/ops/lookup',
  defaults: { q: '' },
  readers: {
    q: readParam.text(LOOKUP_MAX),
  },
};

// --- Withdrawal queue -------------------------------------------------------------------------

/** The withdrawal queue's tabs. The URL calls this `tab`; the API calls it `group`. */
export const WITHDRAWAL_TABS = ['WAITING', 'IN_FLIGHT', 'DONE'] as const;

/** One withdrawal-queue tab. */
export type WithdrawalTab = (typeof WITHDRAWAL_TABS)[number];

/** The columns the withdrawal queue sorts by, server-side. */
export const WITHDRAWAL_SORTS = ['CREATED', 'AMOUNT'] as const;

/** The withdrawal queue's view state. */
export interface WithdrawalListState {
  /** Which tab is open. */
  readonly tab: WithdrawalTab;
  /** A single withdrawal status, or `ALL` for the tab's own set. */
  readonly status: string;
  /** Free-text search over the pool. */
  readonly q: string;
  /** Exact amount as a decimal string, or `''` — satang-precise, unlike the free-text search. */
  readonly amount: string;
  /** Sort column. */
  readonly sort: (typeof WITHDRAWAL_SORTS)[number];
  /** Sort direction, or `undefined` for the tab's natural order. */
  readonly dir: SortDir | undefined;
  /** Zero-based page. */
  readonly page: number;
  /** Rows per page. */
  readonly limit: number;
}

/** The withdrawal queue's URL contract. */
export const WITHDRAWAL_LIST: ListStateDef<WithdrawalListState> = {
  path: '/ops/withdrawals',
  defaults: {
    tab: 'WAITING',
    status: 'ALL',
    q: '',
    amount: '',
    sort: 'CREATED',
    dir: undefined,
    page: 0,
    limit: WITHDRAWALS_PAGE_SIZE,
  },
  readers: {
    tab: readParam.oneOf(WITHDRAWAL_TABS),
    status: readParam.oneOf(['ALL', ...WITHDRAWAL_STATUSES]),
    q: readParam.text(SEARCH_MAX),
    amount: readParam.decimal(16),
    sort: readParam.oneOf(WITHDRAWAL_SORTS),
    dir: readParam.oneOf(SORT_DIRS),
    page: readParam.index(9_999),
    limit: readParam.size(PAGE_SIZES),
  },
};

/** Which tab a withdrawal-queue row's status lives on — WAITING for anything unrecognised. */
const WITHDRAWAL_TAB_OF_STATUS: Readonly<Record<string, WithdrawalTab>> = {
  AVAILABLE: 'WAITING',
  RESERVED: 'IN_FLIGHT',
  LOCKED: 'IN_FLIGHT',
  ON_HOLD: 'IN_FLIGHT',
  FULFILLED: 'DONE',
  CANCELLED: 'DONE',
  EXPIRED: 'DONE',
};

/**
 * The withdrawal queue, filtered to one row and opened on the tab that row actually lives on.
 *
 * The drill-through target for the unified lookup's withdrawal bucket (`/ops/lookup`): a row found
 * there can be waiting, mid-flight or done, and the queue only ever renders one tab at a time, so
 * the link has to pick the right one rather than always landing on WAITING and showing "no
 * results" for anything already matched. Built through {@link listHref} against `WITHDRAWAL_LIST`
 * for the same reason {@link depositorHistoryHref} is: the param names and the defaults that stay
 * out of the URL come from the queue's own contract, not from a hand-built query string.
 *
 * @param id - the withdrawal's id, in full.
 * @param status - the row's wire status, used only to pick the tab.
 */
export function withdrawalQueueHref(id: string, status: string): string {
  const tab = WITHDRAWAL_TAB_OF_STATUS[status] ?? 'WAITING';
  return listHref(WITHDRAWAL_LIST, { ...WITHDRAWAL_LIST.defaults, tab, q: id });
}

// --- History ----------------------------------------------------------------------------------

/** The terminal statuses History filters by; `ALL` means the union, never in-flight rows. */
export const HISTORY_STATUSES = ['ALL', 'COMPLETED', 'REJECTED', 'EXPIRED'] as const;

/** The columns History sorts by, server-side. */
export const HISTORY_SORTS = ['FINISHED', 'AMOUNT'] as const;

/** History's view state. */
export interface HistoryListState {
  /** A terminal status, or `ALL`. */
  readonly status: (typeof HISTORY_STATUSES)[number];
  /** Free text: a fragment of a transaction id, or a depositor account reference in full. */
  readonly q: string;
  /** Exact amount as a decimal string, or `''` — satang-precise, unlike the free-text search. */
  readonly amount: string;
  /** Sort column. */
  readonly sort: (typeof HISTORY_SORTS)[number];
  /** Sort direction, or `undefined` for newest/largest first. */
  readonly dir: SortDir | undefined;
  /** Zero-based page. */
  readonly page: number;
  /** Rows per page. */
  readonly limit: number;
}

/** History's URL contract. */
export const HISTORY_LIST: ListStateDef<HistoryListState> = {
  path: '/ops/history',
  defaults: {
    status: 'ALL',
    q: '',
    amount: '',
    sort: 'FINISHED',
    dir: undefined,
    page: 0,
    limit: HISTORY_PAGE_SIZE,
  },
  readers: {
    status: readParam.oneOf(HISTORY_STATUSES),
    q: readParam.text(SEARCH_MAX),
    amount: readParam.decimal(16),
    sort: readParam.oneOf(HISTORY_SORTS),
    dir: readParam.oneOf(SORT_DIRS),
    page: readParam.index(9_999),
    limit: readParam.size(PAGE_SIZES),
  },
};

/**
 * The History view that answers "what has *this depositor* actually done?".
 *
 * The drill-through target for the abandonment leaderboard, whose rows name accounts rather than
 * transactions: a leaderboard's whole output is a decision about one account, and the next question
 * an operator has after "this one is abandoning slots" is always "show me the deposits behind that
 * number". Before this the answer was to select the reference, switch screens and paste it — three
 * steps in which the operator can also lose the row they were on.
 *
 * Built through {@link listHref} against `HISTORY_LIST` rather than by concatenating `?q=`, which
 * is what makes it a *link into a screen* rather than a guess about one: the param name, the
 * defaults that stay out of the URL, and the order they are written in all come from History's own
 * contract, so renaming a param there breaks this at the type level instead of silently producing a
 * link that lands on an unfiltered list.
 *
 * It relies on one documented property of the far side: History's `q` matches a transaction id by
 * substring **or** a depositor's account reference *by whole value*, trimmed and case-insensitive
 * (see {@link HistoryListState}). Whole value is why the caller must pass the reference exactly as
 * the row holds it — a truncated or prettified ref matches nothing — and it is also what keeps the
 * failure mode honest if the far side ever changes: an operator lands on History with the reference
 * in the search box and an empty result, which reads as "nothing found" rather than as a dead link.
 *
 * **Participant scope is not carried in the link, and does not need to be.** The console's website
 * scope is a session preference held in `lib/scope/web-scope.tsx`, not a param on any screen's URL,
 * and every operator read narrows to it inside the query hooks — so a leaderboard filtered to one
 * site drills through to a History filtered to the same site without either end saying so. Adding
 * a param here would put a value in the address bar that no screen reads, which is exactly the "a
 * value in the URL is a value the screen genuinely offers" rule this module exists to keep.
 *
 * What that leaves is the *unscoped* case, and it has to be lived with rather than papered over: an
 * account reference is unique only within the site that minted it, so with the console showing the
 * whole pool, two participants who both number their customers from 1 make `user_42` match both.
 * That stays *visible* — every History row renders its participant — and the link's own accessible
 * name says it finds deposits *matching a reference* rather than "this depositor's deposits",
 * because an operator who arrived from one specific leaderboard row would otherwise have no reason
 * to check. Scoping the console to that site is the fix, and now it is one.
 *
 * @param accountRef - the depositor's account reference, in full, as the row holds it.
 */
export function depositorHistoryHref(accountRef: string): string {
  return listHref(HISTORY_LIST, { ...HISTORY_LIST.defaults, q: accountRef });
}

// --- Review queue -----------------------------------------------------------------------------

/** The case statuses the review queue filters by. */
export const REVIEW_CASE_STATUSES = [
  'ALL',
  'OPEN',
  'PROPOSED',
  'ESCALATED',
  'CONFIRMED',
  'CLOSED',
] as const;

/** Whether a case is spoken for. */
export const REVIEW_CLAIM_FILTERS = ['ALL', 'UNCLAIMED', 'CLAIMED'] as const;

/** The orders the review queue reads in. Sorting is client-side; the queue is not paginated. */
export const REVIEW_SORTS = ['SLA', 'AGE', 'AMOUNT'] as const;

/** The review queue's view state. */
export interface ReviewListState {
  /** Case status, or `ALL`. */
  readonly status: string;
  /** Review reason, or `ALL`. */
  readonly reason: string;
  /** Claim state, or `ALL`. */
  readonly claimed: string;
  /** Minimum amount as a decimal string, or `''`. */
  readonly min: string;
  /** Exact amount as a decimal string, or `''` — ANDs with `min` rather than replacing it. */
  readonly amount: string;
  /** Free-text search over case and transaction ids. */
  readonly q: string;
  /** Sort column. */
  readonly sort: (typeof REVIEW_SORTS)[number];
  /** Sort direction, or `undefined` for most-urgent/largest first. */
  readonly dir: SortDir | undefined;
}

/** The review queue's URL contract. */
export const REVIEW_LIST: ListStateDef<ReviewListState> = {
  path: '/ops/reviews',
  defaults: {
    status: 'ALL',
    reason: 'ALL',
    claimed: 'ALL',
    min: '',
    amount: '',
    q: '',
    sort: 'SLA',
    dir: undefined,
  },
  readers: {
    status: readParam.oneOf(REVIEW_CASE_STATUSES),
    reason: readParam.oneOf(['ALL', ...REVIEW_REASONS]),
    claimed: readParam.oneOf(REVIEW_CLAIM_FILTERS),
    min: readParam.decimal(16),
    amount: readParam.decimal(16),
    q: readParam.text(SEARCH_MAX),
    sort: readParam.oneOf(REVIEW_SORTS),
    dir: readParam.oneOf(SORT_DIRS),
  },
};

/**
 * The review queue, filtered to one transaction's cases.
 *
 * The link an operator needs when a re-verification puts their transaction back under review: the
 * new case has a *different id*, so the page they are standing on cannot reach it and reloading
 * shows them the closed one. `GET /ops/v1/transactions/{id}` does not carry a review-case id — the
 * contract has no such field — so the honest route to the new case is the queue's own free-text
 * search, which already matches on transaction id.
 *
 * Deliberately not narrowed to `status: 'OPEN'`: the queue then lists the closed case beside the
 * live one, each with its state badge, which is the true picture of what this transaction has been
 * through rather than a filtered view that hides half of it.
 *
 * @param transactionId - the transaction whose cases the queue should show, in full.
 */
export function transactionReviewCasesHref(transactionId: string): string {
  return listHref(REVIEW_LIST, { ...REVIEW_LIST.defaults, q: transactionId });
}

// --- Depositor abandonment --------------------------------------------------------------------

/**
 * The windows the abandonment leaderboard offers, in hours.
 *
 * The same three the dashboard ships — a day, a week, a month — because an operator moving between
 * the two screens is asking one question in two shapes ("is abandonment up?" then "who is doing
 * it?"), and a leaderboard whose "week" meant something else than the dashboard's would make the
 * two impossible to read together.
 *
 * These are the *presets*, not the whole vocabulary: `from`/`to` below carry an absolute range from
 * the same picker the dashboard uses, for the same reason the two screens share their presets — an
 * operator who narrowed the dashboard to the four hours around an incident and then came here to
 * find out who was responsible must be able to ask about those four hours, not about the day that
 * contains them.
 */
export const ABANDONMENT_WINDOWS = [24, 168, 720] as const;

/**
 * The columns the leaderboard orders by, server-side.
 *
 * Re-exported from the contract rather than restated, so a sort key can never reach the URL that
 * the endpoint would reject. `ABANDON_SCORE` is the Wilson lower bound and the default;
 * `ABANDON_RATE` is the raw ratio, offered beside it precisely so an operator can see for
 * themselves what the two orders disagree about.
 */
export const ABANDONMENT_SORTS = DEPOSITOR_ABANDONMENT_SORTS;

/** One of the orders the leaderboard offers. */
export type AbandonmentSort = (typeof ABANDONMENT_SORTS)[number];

/**
 * The orders whose *ranking* inherits the cancelled/timed-out misattribution.
 *
 * Both abandonment behaviours used to write the identical event, so for any window reaching back
 * past the response's `split_reliable_from` every older deliberate release is filed as a timeout,
 * permanently and un-backfillably. `abandonment-page.tsx` documents that at length; what this
 * list adds is the consequence for *sorting*, which is a different question from the one the
 * column marks answer. A caveated column shows a number that is partly wrong. A caveated sort
 * puts the wrong rows at the top — and the top of this table is where an operator decides who
 * gets restricted or blocked.
 *
 * Worked out from what each key orders by, not from which columns happen to wear a mark, because
 * those two sets are genuinely different and that gap is what this exists to close:
 * `TIMEOUT_RATE` has **no column at all** on the leaderboard, yet it is reachable as a sort — from
 * the mobile `SortControl`, and from any hand-edited or pasted URL, since `readParam.oneOf` accepts
 * the contract's full enum. So the one order that is *most* obviously derived from the unreliable
 * half was also the one with no header available to carry the existing caveat.
 *
 * Affected, because the split is the whole input to the comparison:
 *  - `TIMEOUT_RATE` — `timed_out / reservations_total`. Pre-cutover, the numerator absorbs every
 *    deliberate release, so it is inflated by an unknown amount that varies per account. Two rows
 *    with identical real behaviour can rank in either order depending only on how much of their
 *    history sits before the cutover.
 *  - `TIMED_OUT` — the raw reclaim count, inflated exactly the same way.
 *  - `CANCELLED` — the raw release count, deflated by precisely what `TIMED_OUT` gained.
 *
 * Deliberately **not** listed, because marking a sound number is how a caveat gets trained out of
 * an operator's attention:
 *  - `ABANDON_SCORE` and `ABANDON_RATE` — both derive from `cancelled + timed_out`, and that *sum*
 *    was always recorded correctly. Only its division into two was ever unreliable, so a caveated
 *    window still ranks correctly by either of these.
 *  - `RESERVATIONS` — the denominator, untouched by the domain change that added `cause`.
 *  - `LAST_SEEN` — a timestamp; no split anywhere near it.
 */
export const ABANDONMENT_SPLIT_DEPENDENT_SORTS: readonly AbandonmentSort[] = [
  'TIMEOUT_RATE',
  'TIMED_OUT',
  'CANCELLED',
];

/**
 * Whether ordering by `sort` inherits the cancelled/timed-out misattribution.
 *
 * Answers only half the question on its own: an affected sort is harmless over a window that
 * begins after the cutover. The screen ANDs this with "does the window reach back past
 * `split_reliable_from`?" before it says anything, so the caveat appears exactly when the order on
 * screen is actually wrong — see {@link ABANDONMENT_SPLIT_DEPENDENT_SORTS}.
 */
export function isSplitDependentSort(sort: AbandonmentSort): boolean {
  return ABANDONMENT_SPLIT_DEPENDENT_SORTS.includes(sort);
}

/**
 * The smallest sample the leaderboard shows by default.
 *
 * Not zero, and the choice is visible rather than silent — the row count under the table names the
 * threshold in words. One account that reserved once and walked away is a 100 % abandon rate and
 * tells an operator nothing; a page whose first screen is fifty of those is a page nobody opens
 * twice. Three is the point where a rate starts to mean something, and the filter is right there
 * for an operator who wants the tail back.
 */
export const ABANDONMENT_MIN_RESERVATIONS = 3;

/** The abandonment leaderboard's view state. */
export interface AbandonmentListState {
  /** Window length in hours — one of {@link ABANDONMENT_WINDOWS}. Ignored while a range is set. */
  readonly window: number;
  /**
   * Start of an absolute range, ISO-8601 with offset, or `''` for none.
   *
   * `from`/`to` are only meaningful as a pair and only when both survive validation, which is
   * `readCustomRange`'s job rather than a reader's — see `components/layout/range-picker.tsx`. When
   * they do, they take precedence over {@link window}, which stays in the URL untouched so that
   * clearing the range returns the operator to the preset they were last reading rather than to a
   * default chosen when this screen was written.
   */
  readonly from: string;
  /** End of an absolute range, ISO-8601 with offset, or `''` for none. Pairs with {@link from}. */
  readonly to: string;
  /** Hide accounts with fewer reservations than this. */
  readonly min: number;
  /** Sort column. */
  readonly sort: AbandonmentSort;
  /** Sort direction, or `undefined` for the column's natural order (worst/most-recent first). */
  readonly dir: SortDir | undefined;
  /** Zero-based page. */
  readonly page: number;
  /** Rows per page. */
  readonly limit: number;
}

/** The abandonment leaderboard's URL contract. */
export const ABANDONMENT_LIST: ListStateDef<AbandonmentListState> = {
  path: '/ops/abandonment',
  defaults: {
    window: 24,
    from: '',
    to: '',
    min: ABANDONMENT_MIN_RESERVATIONS,
    sort: 'ABANDON_SCORE',
    dir: undefined,
    page: 0,
    limit: ABANDONMENT_PAGE_SIZE,
  },
  readers: {
    window: readParam.size(ABANDONMENT_WINDOWS),
    from: readParam.instant(),
    to: readParam.instant(),
    // `index`, not `size`: the threshold is a free number an operator types, only bounded so a
    // hand-edited URL cannot ask the server for a filter it would have to reject.
    min: readParam.index(9_999),
    sort: readParam.oneOf(ABANDONMENT_SORTS),
    dir: readParam.oneOf(SORT_DIRS),
    page: readParam.index(9_999),
    limit: readParam.size(PAGE_SIZES),
  },
};
